import "./style.scss";

import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Row } from "@amzn/stencil-react-components/layout";
import { trackStructEvent } from "@snowplow/browser-tracker";
import React, { useContext, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { EmbeddedMetric, Logger } from "@/apis/Logger";
import { Chatbot } from "@/components/Chatbot";
import { ChatPanel } from "@/components/Chatbot/ChatPanel";
import { ChatWidget } from "@/components/GetSupport";
import { CHAT_SUPPORT_CONTEXTS_METRICS_MAP } from "@/components/GetSupport/ChatWithITSupport/constants";
import { SelfHelpFAQContainer } from "@/components/GetSupport/SelfHelpFAQ/SelfHelpFAQContainer";
import { isSelfHelpEnabled } from "@/helpers/faq-helper";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import { useFeatureFlag } from "@/stores/contexts/FeatureFlagsContext";
import {
  Day1EventAction,
  Day1EventCategory,
  Day1EventLabel,
} from "@/types/snowplow-events";

import VerifyWithItFlyoutContent from "../VerifyWithItFlyoutContent/VerifyWithItFlyoutContent";

/**
 * Implements the chat support flyout panel element.
 */
export const ChatSupportFlyout = () => {
  const { dispatch, state } = useContext(AppContext);
  const intl = useIntl();

  /**
   * Local state controls rendering (ChatWidget mount timing).
   * Global state.chatInitialized is synced for GetSupportHeaderLink to read.
   * Both are needed: local state preserves the exact render timing that
   * ChatWidget's useMemo depends on for capturing the correct refPage.
   */
  const [initialized, setInitialized] = useState(false);
  const isChatbotEnabled = useFeatureFlag("aiChatbot");
  const [activeTab, setActiveTab] = useState<"chatbot" | "support">(
    isChatbotEnabled ? "chatbot" : "support"
  );
  const [escalationSummary, setEscalationSummary] = useState("");

  useEffect(() => {
    // It takes a second for this state to load from the backend, so we need to reinitalize the activeTab state
    if (isChatbotEnabled) {
      setActiveTab("chatbot");
    }
  }, [isChatbotEnabled]);

  const isSelfHelpEnabledForStep = isSelfHelpEnabled(state.currentStep?.id);
  const showStartChatButton =
    !initialized &&
    !isSelfHelpEnabledForStep &&
    !state.getIdvSupportFlyoutVisible;
  const showIdvFlyoutContent = !initialized && state.getIdvSupportFlyoutVisible;

  const setChatInitializedHandler = () => {
    if (!initialized) {
      setInitialized(true);
      dispatch({ type: ActionType.SET_CHAT_INITIALIZED });

      Logger.publishWarnMetric(
        new EmbeddedMetric("InitializedChatWithSupport", "Count", 1, {
          RefPage: state.chatSupportRefPage,
        }),
        "New hire initiated chat with support",
        undefined,
        {
          amazonUsername: state.amazonUsername,
          region: state.activeRegion,
          refPage: state.chatSupportRefPage,
        }
      );

      if (state.nonDirectedChatSupportContext) {
        const { category, action } =
          CHAT_SUPPORT_CONTEXTS_METRICS_MAP[
            state.nonDirectedChatSupportContext
          ];
        trackStructEvent({
          category,
          action,
          label: Day1EventLabel.StartSupportChat,
        });
      } else {
        trackStructEvent({
          category: Day1EventCategory.PageLevel,
          action: Day1EventAction.ChatWithSupport,
          label: Day1EventLabel.StartSupportChat,
        });
      }
    } else {
      void Logger.info(`Chat already initialized, just showing existing chat`);
    }
  };

  /**
   * Sets the visibility of the chat support flyout on click.
   * @param isVisible Should the panel be visible?
   */
  const setVisibilityHandler = (isVisible: boolean) => {
    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_FLYOUT_VISIBLE,
      chatSupportFlyoutVisible: isVisible,
    });

    dispatch({
      type: ActionType.SET_GET_IDV_SUPPORT_FLYOUT_VISIBLE,
      getIdvSupportFlyoutVisible: false,
    });

    if (!isSelfHelpEnabledForStep) {
      trackStructEvent({
        category: Day1EventCategory.PageLevel,
        action: Day1EventAction.ChatWithSupport,
        label: isVisible
          ? Day1EventLabel.OpenChatSupportPanel
          : Day1EventLabel.CloseChatSupportPanel,
      });
    }
  };

  /**
   * Calculates a class name for the panel contents depending on if the Get Support panel is open or not.
   * The GetSupport panel is a Stencil Flyout component which has a drop shadow by default, however the ChatSupportFlyout component is custom
   * and does not come out of the box with a drop shadow. If the GetSupport panel is open, it will cause a double shadow which is not a consistent experience.
   */
  const getFlyoutContentsClassNameString = () => {
    let classNameString = "custom-flyout-contents ";
    // visible if the panel is opened.
    if (state.chatSupportFlyoutVisible) {
      classNameString += "custom-flyout-contents-visible ";
    }
    // If the chat panel was opened without the get support panel open, it's missing a drop shadow that should be there.
    if (!state.getSupportFlyoutVisible) {
      classNameString += "custom-flyout-contents-visible-drop-shadow ";
    }
    return classNameString;
  };

  /**
   * Switches to the chatbot tab. `source` records how the user got there so the
   * deliberate header button can be told apart from being dropped back when the
   * live chat closes.
   */
  const showChatbot = (source: "tab" | "chat-closed") => {
    trackStructEvent({
      category: Day1EventCategory.Chatbot,
      action: Day1EventAction.ChatbotOpened,
      property: source,
    });
    setActiveTab("chatbot");
  };

  /** Switches from the chatbot back to the already-running live support chat. */
  const showLiveSupport = () => {
    trackStructEvent({
      category: Day1EventCategory.Chatbot,
      action: Day1EventAction.ChatbotReturnedToLiveSupport,
    });
    setActiveTab("support");
  };

  const FlyoutContents = () => {
    return (
      <>
        {isChatbotEnabled && (
          <div
            style={{ display: activeTab === "chatbot" ? "contents" : "none" }}
          >
            <Chatbot
              onContactSupport={(summary) => {
                setEscalationSummary(summary);
                setChatInitializedHandler();
                setActiveTab("support");
              }}
              onClose={() => setVisibilityHandler(false)}
              /*
                Only offer the jump to live support once a Connect session
                actually exists — otherwise the button would land the user on an
                empty "start a chat" screen rather than a conversation.
              */
              onBackToSupport={initialized ? showLiveSupport : undefined}
            />
          </div>
        )}
        <div style={{ display: activeTab === "support" ? "contents" : "none" }}>
          {/*
            Same `ChatPanel` as the chatbot, so both channels present an
            identical header. The back button only appears when there is a
            chatbot to go back to.
          */}
          <ChatPanel
            titleId={getFlyoutTitle()}
            onClose={() => setVisibilityHandler(false)}
            backAction={
              isChatbotEnabled
                ? {
                    labelId: "chatbot-back-to-aiva",
                    onClick: () => showChatbot("tab"),
                    dataTestId: "chatSupportBackToAiva",
                  }
                : undefined
            }
            dataTestId="chatSupportContainer"
            /*
              The Connect widget brings its own full-width chrome, so the body
              inset is dropped while it is mounted. The FAQ, IDV and start-chat
              screens keep the standard padding.
            */
            fullBleedBody={initialized}
          >
            {initialized ? (
              <ChatWidget
                shouldInitialize={initialized}
                setVisibilityHandler={setVisibilityHandler}
                nonDirectedSupportCtx={state.nonDirectedChatSupportContext}
                refPage={state.chatSupportRefPage}
                refLink={state.chatSupportRefLink}
                username={state.amazonUsername}
                escalationSummary={escalationSummary}
                onDisconnect={() => showChatbot("chat-closed")}
              />
            ) : isSelfHelpEnabledForStep ? (
              <SelfHelpFAQContainer
                data-test-id={"selfHelpFAQContainer"}
                onChatClick={setChatInitializedHandler}
              />
            ) : showIdvFlyoutContent ? (
              <VerifyWithItFlyoutContent
                chatInitializedHandler={setChatInitializedHandler}
              />
            ) : showStartChatButton ? (
              <Row justifyContent="center" padding="S300">
                <Button
                  variant={ButtonVariant.Secondary}
                  onClick={setChatInitializedHandler}
                  dataTestId="chatClickToStart"
                  aria-label={intl.formatMessage({
                    id: "arialabels-clickToStartChat",
                  })}
                >
                  <FormattedMessage id="getSupport-chatSupport-clickToStart" />
                </Button>
              </Row>
            ) : null}
          </ChatPanel>
        </div>
      </>
    );
  };

  // Only called for the IT Support tab; the chatbot tab supplies its own title.
  const getFlyoutTitle = () => {
    if (initialized) {
      return "text-chatWithITSupport";
    }

    if (isSelfHelpEnabledForStep) {
      return "getHelp-header";
    }
    return state.getIdvSupportFlyoutVisible
      ? "getSupport-header"
      : "text-chatWithITSupport";
  };

  return (
    <div className="custom-flyout-container">
      {/*
        No click-catching overlay sits to the left of the panel. One used to,
        purely to dismiss on an outside click. It had no background, so once
        outside clicks stopped dismissing it would only have swallowed clicks and
        made the rest of the page unusable. Dismissal is via the close button.
      */}
      <div className={getFlyoutContentsClassNameString()}>
        {/*
          Each tab renders its own `ChatPanel`, which owns the header, the
          scrolling body and the footer. There is no shared header here, so the
          two channels cannot drift apart visually.
        */}
        {FlyoutContents()}
      </div>
    </div>
  );
};
